/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file bonusSlice.js
 * @description Redux slice for managing bonus related state and operations
 */

import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import {getRequestParams} from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the bonus slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = () => ({
  availablePromotions: [],
  error: null,
  loading: false,
});

/**
 * @function getAvailablePromotions
 * @description Async thunk for get available promotions operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getAvailablePromotions = createAsyncThunk("bonus/getAvailablePromotions", async (data, thunkAPI) => {
  try {
    const { accountId, authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(
      `/player/${accountId ? `acc/${accountId}/` : ""}bonus/availablepromotions?originId=${originId}&lineId=${lineId}`,
    );

    return {
      availablePromotions: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain available bonus promotions", // serializable (err.response.data)
      name: "Bonus promotion list Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant bonusSlice
 * @description Redux slice for bonus management
 */
const bonusSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getAvailablePromotions.pending, (state) => {
        state.availablePromotions = [];
        state.error = null;
        state.loading = true;
      })
      .addCase(getAvailablePromotions.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getAvailablePromotions.fulfilled, (state, action) => {
        state.availablePromotions = action.payload.availablePromotions;
        state.loading = false;
        state.error = null;
      });
  },
  initialState: getInitialState(),
  name: "bonus",
  reducers: {},
});
const { reducer } = bonusSlice;
export default reducer;
